// @flow
import { DateTime } from 'luxon';
import { CoaTypeEnum } from 'data/chartOfAccounts/types';
import type { ChartOfAccount } from 'data/chartOfAccounts/types';
import { getBalanceAdjustmentString } from 'data/chartOfAccounts/utils';
import { isSplitTxn } from 'data/transactions/utils';
import { isAcme, isQuicken } from 'isAcme';
import memoizeOne from 'memoize-one';
import { getAccountById } from 'data/accounts/retrievers';
import { getAccountTypeNodeByQcsType } from 'data/accounts/types';
import { isIncomeCat } from 'data/categories/retrievers';
import { RRule } from 'rrule';
import numeral from 'numeral';
import { STTypeEnum, FrequencyEnum } from './scheduledTransactionsTypes';
import type { STType, ScheduledTransaction, RecurrenceType } from './scheduledTransactionsTypes';

export const frequencyAliasTypes = {
  everyWeekACME: 'Every week',
  everyTwoWeeksACME: 'Every two weeks',
  twiceAMonthACME: 'Twice a month',
  everyMonthACME: 'Every month',
  every2MonthsACME: 'Every 2 months',
  everyQuarterACME: 'Every Quarter',
  every6MonthsACME: 'Every 6 months',
  everyYearACME: 'Every year',

  weeklyQuicken: 'Weekly',
  every2WeeksQuicken: 'Every two weeks',
  twiceAMonthQuicken: 'Twice a month',
  everyFourWeeksQuicken: 'Every four weeks',
  monthlyQuicken: 'Monthly',
  quarterlyQuicken: 'Quarterly',
  twiceAYearQuicken: 'Twice a year',
  toPayEstimatedTaxesQuicken: 'To pay estimated taxes',
  yearlyQuicken: 'Yearly',
  onlyOnce: 'Only once',

  custom: 'Custom',
};

const frequencyAliasesACME = [
  frequencyAliasTypes.everyWeekACME,
  frequencyAliasTypes.everyTwoWeeksACME,
  frequencyAliasTypes.twiceAMonthACME,
  frequencyAliasTypes.everyMonthACME,
  frequencyAliasTypes.every2MonthsACME,
  frequencyAliasTypes.everyQuarterACME,
  frequencyAliasTypes.every6MonthsACME,
  frequencyAliasTypes.everyYearACME,
];
const frequencyAliasesQuicken = [
  frequencyAliasTypes.weeklyQuicken,
  frequencyAliasTypes.every2WeeksQuicken,
  frequencyAliasTypes.twiceAMonthQuicken,
  frequencyAliasTypes.everyFourWeeksQuicken,
  frequencyAliasTypes.monthlyQuicken,
  frequencyAliasTypes.quarterlyQuicken,
  frequencyAliasTypes.twiceAYearQuicken,
  frequencyAliasTypes.toPayEstimatedTaxesQuicken,
  frequencyAliasTypes.yearlyQuicken,
  frequencyAliasTypes.onlyOnce,
];
export const frequencyAliases = (isAcme && frequencyAliasesACME) || (isQuicken && frequencyAliasesQuicken);

export const weekDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export const weekDaysLong = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const recurrenceFromFrequencyAlias = (frequencyAlias: any, dueOn: string, overwriteInterval: any) => {
  let recurrence;

  if (dueOn && frequencyAlias) {
    const date = DateTime.fromISO(dueOn);

    if (isAcme) {
      switch (frequencyAlias) {
        case frequencyAliasTypes.everyWeekACME: {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: 'WEEKLY',
            interval: 1,
            ...(day && {
              byDay: [day],
            }),
          };
        }
          break;
        case frequencyAliasTypes.everyTwoWeeksACME: {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: 'WEEKLY',
            interval: 2,
            ...(day && {
              byDay: [day],
            }),
          };
        }
          break;
        case frequencyAliasTypes.twiceAMonthACME: {
          const day1 = date && date.day && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          const date2 = date.plus({ days: 15 });
          const day2 = date2 && date2.day && (date2.day < 29 ? date2.day : date2.day - date2.daysInMonth - 1);
          recurrence = {
            frequency: 'MONTHLY',
            interval: 1,
            ...(day1 && day2 ? {
              byMonthDay: [day1, day2],
            } : undefined),
          };
        }
          break;
        case frequencyAliasTypes.everyMonthACME: {
          const day = date && date.day && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: 'MONTHLY',
            interval: 1,
            ...(day ? {
              byMonthDay: [day],
            } : undefined),
          };
        }
          break;
        case frequencyAliasTypes.every2MonthsACME: {
          const day = date && date.day && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: 'MONTHLY',
            interval: 2,
            ...(day ? {
              byMonthDay: [day],
            } : undefined),
          };
        }
          break;
        case frequencyAliasTypes.everyQuarterACME: {
          const day = date && date.day && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: 'MONTHLY',
            interval: 3,
            ...(day ? {
              byMonthDay: [day],
            } : undefined),
          };
        }
          break;
        case frequencyAliasTypes.every6MonthsACME: {
          const day = date && date.day && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: 'MONTHLY',
            interval: 6,
            ...(day ? {
              byMonthDay: [day],
            } : undefined),
          };
        }
          break;
        case frequencyAliasTypes.everyYearACME: {
          const day = date && date.day && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: 'MONTHLY',
            interval: 12,
            ...(day ? {
              byMonthDay: [day],
            } : undefined),
          };
        }
          break;
        default:
      }
    }

    if (isQuicken) {
      switch (frequencyAlias) {
        case frequencyAliasTypes.weeklyQuicken:{
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: 'WEEKLY',
            interval: overwriteInterval || 1,
            ...(day && {
              byDay: [day],
            }),
          };
        }
          break;
        case frequencyAliasTypes.every2WeeksQuicken:{
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: 'WEEKLY',
            interval: 2,
            ...(day && {
              byDay: [day],
            }),
          };
        }
          break;
        case frequencyAliasTypes.twiceAMonthQuicken: {
          const day1 = date && date.day;
          const date2 = date.plus({ days: 14 });
          const day2 = day1 !== 15 ? date2 && date2.day : date.daysInMonth;
          recurrence = {
            frequency: 'MONTHLY',
            interval: overwriteInterval || 1,
            ...(day1 && day2 ? {
              byMonthDayOrLastDay: [day1, day2],
            } : undefined),
          };
        }
          break;
        case frequencyAliasTypes.everyFourWeeksQuicken: {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: 'WEEKLY',
            interval: 4,
            ...(day && {
              byDay: [day],
            }),
          };
        }
          break;
        case frequencyAliasTypes.monthlyQuicken: {
          const day = date && date.day;
          recurrence = {
            frequency: 'MONTHLY',
            interval: overwriteInterval || 1,
            ...(day && {
              byMonthDay: [day],
            }),

          };
        }
          break;
        case frequencyAliasTypes.quarterlyQuicken: {
          const date1 = date.toFormat('LL/dd');
          const date2 = date.plus({ months: 3 }).toFormat('LL/dd');
          const date3 = date.plus({ months: 6 }).toFormat('LL/dd');
          const date4 = date.plus({ months: 9 }).toFormat('LL/dd');
          recurrence = {
            frequency: 'YEARLY',
            interval: 3,
            ...(date1 && date2 && date3 && date4 && {
              byDates: [date1, date2, date3, date4],
            }),
          };
        }
          break;
        case frequencyAliasTypes.twiceAYearQuicken: {
          const date6Months = date.plus({ months: 6 });
          let date1 = date.toFormat('LL/dd');
          let date2 = date6Months.toFormat('LL/dd');

          if (date.year < date6Months.year) {
            date2 = date1;
            date1 = date6Months.toFormat('LL/dd');
          }

          recurrence = {
            frequency: 'YEARLY',
            interval: overwriteInterval || 1,
            ...(date1 && date2 ? {
              byDates: [date1, date2],
            } : undefined),
          };
        }
          break;
        case frequencyAliasTypes.toPayEstimatedTaxesQuicken:
          recurrence = {
            frequency: 'YEARLY',
            interval: 1,
            byDates: ['01/15', '04/15', '06/15', '09/15'],
          };
          break;
        case frequencyAliasTypes.yearlyQuicken:
          recurrence = {
            frequency: 'YEARLY',
            interval: overwriteInterval || 1,
          };
          break;
        case frequencyAliasTypes.onlyOnce:
          recurrence = {
            frequency: 'YEARLY',
            interval: 1,
            count: 1,
          };
          break;
        default:
      }
    }
  }

  return recurrence;
};

export const frequencyAliasFromRecurrence = (recurrence: any) => {
  let frequencyAlias;

  if (recurrence
    && recurrence.frequency
    && recurrence.interval
  ) {
    if (isAcme) {
      if (recurrence.frequency === 'WEEKLY' && recurrence.interval === 1) {
        frequencyAlias = frequencyAliasTypes.everyWeekACME;
      } else if (recurrence.frequency === 'WEEKLY' && recurrence.interval === 2) {
        frequencyAlias = frequencyAliasTypes.everyTwoWeeksACME;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval === 1 && recurrence.byMonthDay && recurrence.byMonthDay.length === 2) {
        frequencyAlias = frequencyAliasTypes.twiceAMonthACME;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval === 1) {
        frequencyAlias = frequencyAliasTypes.everyMonthACME;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval === 6) {
        frequencyAlias = frequencyAliasTypes.every6MonthsACME;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval === 2) {
        frequencyAlias = frequencyAliasTypes.every2MonthsACME;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval === 3) {
        frequencyAlias = frequencyAliasTypes.everyQuarterACME;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval === 12) {
        frequencyAlias = frequencyAliasTypes.everyYearACME;
      } else {
        frequencyAlias = frequencyAliasTypes.custom;
      }
    }
    if (isQuicken) {
      if (recurrence.frequency === 'WEEKLY' && recurrence.interval === 1) {
        frequencyAlias = frequencyAliasTypes.weeklyQuicken;
      } else if (recurrence.frequency === 'WEEKLY' && recurrence.interval === 2) {
        frequencyAlias = frequencyAliasTypes.every2WeeksQuicken;
      } else if (recurrence.frequency === 'WEEKLY' && recurrence.interval === 4) {
        frequencyAlias = frequencyAliasTypes.everyFourWeeksQuicken;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval === 1 &&
        ((recurrence.byMonthDayOrLastDay && recurrence.byMonthDayOrLastDay.length === 2) ||
          recurrence.byMonthDay && recurrence.byMonthDay.length === 2)) {
        frequencyAlias = frequencyAliasTypes.twiceAMonthQuicken;
      } else if (recurrence.frequency === 'MONTHLY' && recurrence.interval >= 1 && recurrence.interval <= 12 ) {
        frequencyAlias = frequencyAliasTypes.monthlyQuicken;
      } else if (recurrence.frequency === 'YEARLY' && recurrence.interval === 3) {
        frequencyAlias = frequencyAliasTypes.quarterlyQuicken;
      } else if (recurrence.frequency === 'YEARLY' && recurrence.interval === 1 && recurrence.count === 1) {
        frequencyAlias = frequencyAliasTypes.onlyOnce;
      } else if (recurrence.frequency === 'YEARLY' && recurrence.interval === 1 && recurrence.byDates && recurrence.byDates.length === 2) {
        frequencyAlias = frequencyAliasTypes.twiceAYearQuicken;
      } else if (recurrence.frequency === 'YEARLY' && recurrence.interval === 1 && recurrence.byDates && recurrence.byDates.length === 4) {
        frequencyAlias = frequencyAliasTypes.toPayEstimatedTaxesQuicken;
      } else if (recurrence.frequency === 'YEARLY' && recurrence.interval === 1) {
        frequencyAlias = frequencyAliasTypes.yearlyQuicken;
      } else {
        frequencyAlias = frequencyAliasTypes.custom;
      }
    }
  }

  return frequencyAlias;
};

export const stTypes = (isAcme ?
  [
    STTypeEnum.BILL,
    STTypeEnum.SUBSCRIPTION,
    STTypeEnum.INCOME,
    STTypeEnum.TRANSFER,
  ] : [
    STTypeEnum.EXPENSE,
    STTypeEnum.INCOME,
    STTypeEnum.TRANSFER,
  ]);
export const STTypeNamesEnum = Object.freeze({
  [STTypeEnum.EXPENSE]: 'Bill', // Expense
  [STTypeEnum.INCOME]: 'Income',
  [STTypeEnum.TRANSFER]: 'Transfer',
  [STTypeEnum.BILL]: 'Bill',
  [STTypeEnum.SUBSCRIPTION]: 'Subscription',
  [STTypeEnum.UNKNOWN]: 'Unknown',
});

export const typeFromScheduledTransaction = (scheduledTransaction: ScheduledTransaction): STType => {
  let stType: STType = STTypeEnum.UNKNOWN;

  if (scheduledTransaction.type !== STTypeEnum.UNKNOWN) {
    stType = scheduledTransaction.type;
  } else if (scheduledTransaction.transaction && scheduledTransaction.transaction.isBill) {
    stType = STTypeEnum.BILL;
  } else if (scheduledTransaction.transaction && scheduledTransaction.transaction.isSubscription) {
    stType = STTypeEnum.SUBSCRIPTION;
  } else if (scheduledTransaction && scheduledTransaction.transaction && scheduledTransaction.transaction.coa) {
    const { coa } = scheduledTransaction.transaction;
    stType = typeFromCOA(coa);
  }

  return stType;
};

export const typeFromCOA = (coa: ChartOfAccount): STType => {
  let stType = STTypeEnum.UNKNOWN;

  if (coa) {
    if (coa.type === CoaTypeEnum.CATEGORY && isIncomeCat(coa.id)) {
      stType = STTypeEnum.INCOME;
    } else if (coa.type === CoaTypeEnum.ACCOUNT || coa.type === CoaTypeEnum.BALANCE_ADJUSTMENT) {
      stType = STTypeEnum.TRANSFER;
    } else {
      stType = isAcme ? STTypeEnum.BILL : STTypeEnum.EXPENSE;
    }
  }

  return stType;
};

export const negatorFromCOA = (coa: ChartOfAccount) => {
  let negator = -1;

  if (coa && coa.type === CoaTypeEnum.CATEGORY && isIncomeCat(coa.id)) {
    negator = +1;
  } else {
    negator = -1;
  }

  return negator;
}

export const negatorFromType = (type: STType) => {
  let negator = -1;

  switch (type) {
    case STTypeEnum.BILL:
    case STTypeEnum.SUBSCRIPTION:
    case STTypeEnum.TRANSFER:
    case STTypeEnum.EXPENSE:
    case STTypeEnum.UNKNOWN:
      negator = -1;
      break;

    case STTypeEnum.INCOME:
      negator = +1;
      break;

    default:
      negator = -1;
  }

  return negator;
};

export const getSchedTxnType = (txn) => {

  if (txn.isBill) {
    return 'bill';
  }
  if (txn.isSubscription) {
    return 'subscription';
  }
  if (isSplitTxn(txn)) {
    return 'item';
  }
  if (txn.coa.type === 'ACCOUNT') {
    return 'transfer';
  }
  if (txn.coa.type === 'CATEGORY') {
    return isIncomeCat(txn.coa.id) ? 'income' : 'bill';
  }
  return 'item';
};

export const getCategoryString = (schTxn, categoriesById, accountsById) => {
  const coa = schTxn && schTxn.coa;
  if(!coa) {
    return 'Not Found'
  }

  if(isSplitTxn(schTxn)) {
    return '--- Split ---';
  }

  const category = {
    [CoaTypeEnum.UNCATEGORIZED]: () => 'Uncategorized',
    [CoaTypeEnum.BALANCE_ADJUSTMENT]: () => getBalanceAdjustmentString(coa.id),
    [CoaTypeEnum.ACCOUNT]: () => {
      const acct = accountsById.get(coa.id);
      return acct && acct.name;
    },
    default: () => {
      const cat = categoriesById.get(coa.id);

      return cat && cat.name;
    },
  };

  return (category[coa.type] || category.default)() || 'Not Found';
};
// This getter will return a string with the next payment instance and date of the scheduled transaction
const getNextDateFormatted = (firstUpcomingAmount, dueOn) => {
  if (dueOn && firstUpcomingAmount) { // If date and amount exist
    return `Next: ${numeral(firstUpcomingAmount).format('$0,0.00')} on ${DateTime.fromISO(dueOn).toLocaleString(DateTime.DATE_MED)}`;
  }
  if (dueOn) return `Next: ${DateTime.fromISO(dueOn).toLocaleString(DateTime.DATE_MED)}`; // If only date exists
  if (firstUpcomingAmount) return `Next: ${numeral(firstUpcomingAmount).format('$0,0.00')}`; // If only amount exists
  return 'Next: Unknown'; // error case
};
export const userFriendlyTxnNames = memoizeOne((scheduledtxn, categoriesById, accountsById, firstUpcomingTxn) => {
  const accountInfo = scheduledtxn && scheduledtxn.transaction &&
    scheduledtxn.transaction.accountId && getAccountById(scheduledtxn.transaction.accountId);
  const accountTypeNode = scheduledtxn && scheduledtxn.transaction &&
    scheduledtxn.transaction.accountId && accountInfo &&
    getAccountTypeNodeByQcsType(accountInfo.type, accountInfo.subType);
  const scheduleTxnType = scheduledtxn.type;
  const dueOn = (firstUpcomingTxn && firstUpcomingTxn.postedOn) || (scheduledtxn.dueOn);
  // used to show firstUpcomingTransaction.amount in Simplifi if it existed. Now show same across WF/Simplifi
  const amount = scheduledtxn.transaction && scheduledtxn.transaction.amount;
  const firstUpcomingAmount = firstUpcomingTxn ? Math.abs(firstUpcomingTxn.amount): '';
  return {
    id: scheduledtxn && scheduledtxn.id,
    clientId: scheduledtxn.transaction && scheduledtxn.transaction.clientId,
    accountId: scheduledtxn.transaction && scheduledtxn.transaction.accountId,
    payeeName: (scheduledtxn.transaction && scheduledtxn.transaction.payee) || 'New Series',
    amount: amount || '',
    accountName: (accountInfo && accountInfo.name) || '[Account Not Synced]',
    frequency: frequencyAliasFromRecurrence(scheduledtxn.recurrence) || 'Unknown',
    nextDate: dueOn && DateTime.fromISO(dueOn).toLocaleString(),
    nextDateFormatted: getNextDateFormatted(firstUpcomingAmount, dueOn),
    endDate:
      scheduledtxn.recurrence && scheduledtxn.recurrence.endOn &&
      DateTime.fromISO(scheduledtxn.recurrence.endOn).toLocaleString(),
    endDateFormatted:
      scheduledtxn.recurrence && scheduledtxn.recurrence.endOn &&
      DateTime.fromISO(scheduledtxn.recurrence.endOn).toLocaleString(DateTime.DATE_MED),
    subtype: scheduleTxnType === STTypeEnum.TRANSFER
      ? 'Transfer from'
      : accountTypeNode && accountTypeNode.displayName,
    type: scheduleTxnType === STTypeEnum.TRANSFER
      ? 'Transfer to'
      : scheduleTxnType,
    category: getCategoryString(scheduledtxn.transaction, categoriesById, accountsById),
  };
});

// THIS IS NOT COMPLETE, will need to add additional cases for various recurrence types
export const calcMontlyAverageForScheduledTxn = (schedTxn) => {

  let { amount } = schedTxn.transaction;
  switch(schedTxn.recurrence.frequency) {
    case 'WEEKLY':
      amount *= 4;
      break;
    case 'YEARLY':
      amount = amount ? amount / 12 : amount;
      break;
    case 'DAILY':
      amount *= 30;
      break;
    case 'MONTHLY':
      if (schedTxn.recurrence.byMonthDayOrLastDay && schedTxn.recurrence.byMonthDayOrLastDay.length > 1) {
        amount *= schedTxn.recurrence.byMonthDayOrLastDay.length;
      }
      break;

    default:
      break;
  }
  return amount / (schedTxn.recurrence.interval || 1);

};

const weekDaysMap = Object.freeze({
  'MO': RRule.MO,
  'TU': RRule.TU,
  'WE': RRule.WE,
  'TH': RRule.TH,
  'FR': RRule.FR,
  'SA': RRule.SA,
  'SU': RRule.SU,
});

export const rruleFromRecurrence = (recurrence: RecurrenceType) => {
  if (!recurrence) {
    return null;
  }

  let freq;
  switch(recurrence.frequency) {
    case FrequencyEnum.WEEKLY:
      freq = RRule.WEEKLY;
      break;
    case FrequencyEnum.YEARLY:
      freq = RRule.YEARLY;
      break;
    case FrequencyEnum.MONTHLY:
      freq = RRule.MONTHLY;
      break;
    case FrequencyEnum.DAILY:
      freq = RRule.DAILY;
      break;
    default:
      assert(false, `unknown frequency = ${recurrence.frequency}`);
  }

  return {
    freq,
    interval: recurrence.interval,
    bymonth: recurrence.byMonth,
    bymonthday: recurrence.byMonthDay || recurrence.byMonthDayOrLastDay,
    byyearday: recurrence.byYearDay,
    byweekday: recurrence.byDay ? recurrence.byDay.map((weekDay) => weekDaysMap[weekDay]) : undefined,
  };
};
